import { useState, useEffect, useCallback } from 'react';
import {
  fetchProjects,
  createProject,
  updateProject,
  deleteProject,
  type Project,
} from '@/lib/api';

const categories = ['All', 'Websites', 'AI', 'E-commerce', 'Marketing', 'Automation', 'Other'];

const emptyForm = { name: '', link: '', description: '', category: 'Websites', tech: '', featured: false };

export default function AdminPortfolio() {
  const [items, setItems] = useState<Project[]>([]);
  const [activeCategory, setActiveCategory] = useState('All');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState<Project | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const loadProjects = useCallback(async (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    setError('');
    try {
      const data = await fetchProjects();
      setItems(data);
    } catch {
      setError('Unable to fetch projects.');
      setItems([]);
    } finally {
      if (showSpinner) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProjects(true);

    const handleUpdate = () => {
      loadProjects(false);
    };

    window.addEventListener('projects_updated', handleUpdate);
    window.addEventListener('storage', handleUpdate);

    return () => {
      window.removeEventListener('projects_updated', handleUpdate);
      window.removeEventListener('storage', handleUpdate);
    };
  }, [loadProjects]);

  const filtered = activeCategory === 'All'
    ? items
    : items.filter((i) => (i.category || 'Other').trim().toLowerCase() === activeCategory.toLowerCase());

  const handleAdd = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    setError('');
    try {
      const res = await createProject({
        name: form.name.trim(),
        link: form.link.trim() || undefined,
        description: form.description.trim() || undefined,
        category: form.category,
        tech: form.tech,
        featured: form.featured,
      });

      if (!res.success) {
        throw new Error(res.error || 'Failed to add project');
      }

      setForm(emptyForm);
      setShowAdd(false);
      loadProjects();
    } catch (err: any) {
      setError(err?.message || 'Failed to add project. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!editing || !editing.name.trim()) return;
    setSaving(true);
    setError('');
    try {
      const res = await updateProject(editing.id, {
        name: editing.name.trim(),
        link: editing.link?.trim() || null,
        description: editing.description?.trim() || null,
        category: editing.category,
        tech: editing.tech,
        featured: editing.featured,
      });

      if (!res.success) {
        throw new Error(res.error || 'Failed to update project');
      }

      setEditing(null);
      loadProjects();
    } catch (err: any) {
      setError(err?.message || 'Failed to update project. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteItem = async (id: string) => {
    setError('');
    try {
      const success = await deleteProject(id);
      if (!success) throw new Error('Delete failed');
      loadProjects();
    } catch {
      setError('Failed to delete project. Please try again.');
    }
  };

  const handleToggleFeatured = async (item: Project) => {
    setError('');
    try {
      const res = await updateProject(item.id, { featured: !item.featured });
      if (!res.success) throw new Error('Toggle failed');
      loadProjects();
    } catch {
      setError('Failed to update project. Please try again.');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-white">Portfolio Management</h1>
          <p className="text-sm text-white/50 mt-1">Add, edit, and organize portfolio projects by category.</p>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="btn-primary text-white font-semibold px-5 py-2.5 rounded-md text-sm whitespace-nowrap inline-flex items-center justify-center gap-2"
        >
          <i className="ri-add-line" /> Add Project
        </button>
      </div>

      {error && (
        <div className="px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm flex items-center justify-between">
          <span>{error}</span>
          <button onClick={fetchProjects} className="text-red-300 hover:text-red-200 font-medium">
            Retry
          </button>
        </div>
      )}

      {/* Category Filters */}
      <div className="flex flex-wrap gap-2">
        {categories.map((c) => (
          <button
            key={c}
            onClick={() => setActiveCategory(c)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition whitespace-nowrap ${
              activeCategory === c
                ? 'bg-neon-500/20 text-neon-300 border border-neon-500/40'
                : 'glass text-white/60 border border-white/5 hover:border-white/20 hover:text-white'
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-20 text-center text-white/50 text-sm">
          <i className="ri-loader-4-line animate-spin mr-2" />
          Loading projects...
        </div>
      ) : filtered.length === 0 ? (
        <div className="glass rounded-xl border border-white/5 p-12 text-center">
          <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-neon-500/10 text-neon-400 text-2xl mb-4">
            <i className="ri-briefcase-4-line" />
          </div>
          <h3 className="font-display font-semibold text-white">No projects yet</h3>
          <p className="mt-2 text-sm text-white/50">Click "Add Project" to create your first portfolio item.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((item) => (
            <div key={item.id} className="glass rounded-xl border border-white/5 overflow-hidden group">
              <div className="relative aspect-[4/3] overflow-hidden">
                <img src={item.image || ''} alt={item.name} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink-950/80 to-transparent opacity-0 group-hover:opacity-100 transition" />
                <div className="absolute top-3 right-3 flex gap-2 opacity-0 group-hover:opacity-100 transition">
                  <button
                    onClick={() => setEditing(item)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/20 text-white backdrop-blur transition"
                  >
                    <i className="ri-edit-line text-sm" />
                  </button>
                  <button
                    onClick={() => handleDeleteItem(item.id)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-400 backdrop-blur transition"
                  >
                    <i className="ri-delete-bin-line text-sm" />
                  </button>
                </div>
                {item.featured && (
                  <div className="absolute top-3 left-3 px-2 py-1 rounded-md bg-neon-500/20 border border-neon-500/30 text-neon-300 text-xs font-semibold">
                    Featured
                  </div>
                )}
              </div>
              <div className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-display font-semibold text-white">{item.name}</h3>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-white/50">
                    {item.category || 'Other'}
                  </span>
                </div>
                {item.link && (
                  <a
                    href={item.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs text-neon-300 hover:text-neon-200 transition mb-2"
                  >
                    <i className="ri-external-link-line" />
                    <span className="truncate max-w-[200px]">{item.link}</span>
                  </a>
                )}
                <p className="text-xs text-white/50 leading-relaxed line-clamp-2">
                  {item.description || 'No description'}
                </p>
                <div className="mt-3 flex items-center justify-between">
                  <div className="flex gap-1 flex-wrap">
                    {(item.tech || []).slice(0, 3).map((t) => (
                      <span key={t} className="text-xs text-white/40 font-mono">{t}</span>
                    ))}
                  </div>
                  <button
                    onClick={() => handleToggleFeatured(item)}
                    className={`text-xs px-2 py-1 rounded-lg transition ${
                      item.featured
                        ? 'text-neon-400 bg-neon-500/10'
                        : 'text-white/40 hover:text-white/60'
                    }`}
                  >
                    {item.featured ? 'Featured' : 'Set Featured'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Project Modal */}
      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="glass rounded-2xl border border-white/10 max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display font-semibold text-lg text-white">Add New Project</h2>
              <button onClick={() => setShowAdd(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/5 text-white/50 hover:text-white transition">
                <i className="ri-close-line" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-white/70 mb-2">Project Name *</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:border-neon-500 outline-none transition"
                  placeholder="e.g. NovaBank Platform"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-white/70 mb-2">Project Link</label>
                <input
                  value={form.link}
                  onChange={(e) => setForm({ ...form, link: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:border-neon-500 outline-none transition"
                  placeholder="https://example.com"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-white/70 mb-2">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:border-neon-500 outline-none transition resize-none"
                  placeholder="Short project description..."
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-white/70 mb-2">Category</label>
                <select
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:border-neon-500 outline-none transition"
                >
                  {categories.filter((c) => c !== 'All').map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-white/70 mb-2">Tech Stack (comma separated)</label>
                <input
                  value={form.tech}
                  onChange={(e) => setForm({ ...form, tech: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:border-neon-500 outline-none transition"
                  placeholder="React, Node.js, PostgreSQL"
                />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.featured}
                  onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                  className="w-4 h-4 rounded border-white/20 bg-white/5 text-neon-500 focus:ring-neon-500"
                />
                <span className="text-sm text-white/70">Mark as featured</span>
              </label>
              <div className="flex gap-3 pt-2">
                <button onClick={handleAdd} disabled={saving} className="flex-1 btn-primary text-white font-semibold px-4 py-2.5 rounded-md text-sm whitespace-nowrap disabled:opacity-50">
                  {saving ? 'Adding...' : 'Add Project'}
                </button>
                <button onClick={() => setShowAdd(false)} className="flex-1 px-4 py-2.5 rounded-md text-sm font-semibold border border-white/10 text-white/70 hover:bg-white/5 transition whitespace-nowrap">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="glass rounded-2xl border border-white/10 max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display font-semibold text-lg text-white">Edit Project</h2>
              <button onClick={() => setEditing(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/5 text-white/50 hover:text-white transition">
                <i className="ri-close-line" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-white/70 mb-2">Project Name *</label>
                <input
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:border-neon-500 outline-none transition"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-white/70 mb-2">Project Link</label>
                <input
                  value={editing.link || ''}
                  onChange={(e) => setEditing({ ...editing, link: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:border-neon-500 outline-none transition"
                  placeholder="https://example.com"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-white/70 mb-2">Description</label>
                <textarea
                  value={editing.description || ''}
                  onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:border-neon-500 outline-none transition resize-none"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-white/70 mb-2">Category</label>
                <select
                  value={editing.category || 'Websites'}
                  onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:border-neon-500 outline-none transition"
                >
                  {categories.filter((c) => c !== 'All').map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-white/70 mb-2">Tech Stack (comma separated)</label>
                <input
                  value={(editing.tech || []).join(', ')}
                  onChange={(e) => setEditing({ ...editing, tech: e.target.value.split(',').map((t) => t.trim()).filter(Boolean) })}
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:border-neon-500 outline-none transition"
                />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editing.featured}
                  onChange={(e) => setEditing({ ...editing, featured: e.target.checked })}
                  className="w-4 h-4 rounded border-white/20 bg-white/5 text-neon-500 focus:ring-neon-500"
                />
                <span className="text-sm text-white/70">Mark as featured</span>
              </label>
              <div className="flex gap-3 pt-2">
                <button onClick={handleUpdate} disabled={saving} className="flex-1 btn-primary text-white font-semibold px-4 py-2.5 rounded-md text-sm whitespace-nowrap disabled:opacity-50">
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
                <button onClick={() => setEditing(null)} className="flex-1 px-4 py-2.5 rounded-md text-sm font-semibold border border-white/10 text-white/70 hover:bg-white/5 transition whitespace-nowrap">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}